import type { IconType } from "react-icons";
import { FaAward, FaBell, FaClone, FaBullhorn, FaClock, FaComment, FaEnvelope, FaQuestion, FaShareAlt, FaShoppingBasket, FaSignature, FaTasks, FaTimes, FaUserEdit } from "react-icons/fa";
import { NotificationTypeDto } from "../../autogen/bff-api";

export type NotificationChipColor = "teal" | "amber" | "blue" | "green" | "red" | "violet" | "neutral";
export interface NotificationVisual { icon: IconType; colorScheme: NotificationChipColor; }

const T = NotificationTypeDto;
const MAP: Partial<Record<NotificationTypeDto, NotificationVisual>> = {
  [T.ContractDateReminder]: { icon: FaClock, colorScheme: "teal" },
  [T.ContractTaskDueDateReminder]: { icon: FaTasks, colorScheme: "amber" },
  [T.ContractTaskDelegated]: { icon: FaTasks, colorScheme: "amber" },
  [T.ContractTaskStatusUpdated]: { icon: FaTasks, colorScheme: "amber" },
  [T.ContractTaskCommentAdded]: { icon: FaComment, colorScheme: "amber" },
  [T.ContractTaskDeleted]: { icon: FaTasks, colorScheme: "amber" },
  [T.ContractTaskDueDateUpdated]: { icon: FaTasks, colorScheme: "amber" },
  [T.ContractSharedWithDepartment]: { icon: FaShareAlt, colorScheme: "blue" },
  [T.ContractSharedWithOrg]: { icon: FaShareAlt, colorScheme: "blue" },
  [T.ContractSharedWithPerson]: { icon: FaShareAlt, colorScheme: "blue" },
  [T.ContractMessageReceived]: { icon: FaComment, colorScheme: "blue" },
  [T.ContractDocumentCompletedSigning]: { icon: FaSignature, colorScheme: "green" },
  [T.ContractSignatureDeadlineExpired]: { icon: FaSignature, colorScheme: "red" },
  [T.SeV2InformationPublished]: { icon: FaBullhorn, colorScheme: "teal" },
  [T.SeV2AwardDecided]: { icon: FaAward, colorScheme: "violet" },
  [T.SeV2AwardLetterDispatched]: { icon: FaAward, colorScheme: "violet" },
  [T.SeV2AwardDecisionRevoked]: { icon: FaAward, colorScheme: "amber" },
  [T.SeV2OrganizationInvite]: { icon: FaShoppingBasket, colorScheme: "blue" },
  [T.SeV2InvitedToTender]: { icon: FaShoppingBasket, colorScheme: "blue" },
  [T.SeV2InvitedToConfirmInterest]: { icon: FaShoppingBasket, colorScheme: "blue" },
  [T.SeV2RoundPublished]: { icon: FaShoppingBasket, colorScheme: "blue" },
  [T.SeV2BidDeadlineReminder]: { icon: FaShoppingBasket, colorScheme: "amber" },
  [T.SeV2BidDelivered]: { icon: FaEnvelope, colorScheme: "green" },
  [T.SeV2EventCancelled]: { icon: FaTimes, colorScheme: "red" },
  [T.SeV2EventClosed]: { icon: FaTimes, colorScheme: "neutral" },
  [T.SeV2NewQuestion]: { icon: FaQuestion, colorScheme: "violet" },
  [T.SeV2QuestionAnswered]: { icon: FaQuestion, colorScheme: "violet" },
  [T.SeV2ClarificationMessageToSupplier]: { icon: FaComment, colorScheme: "violet" },
  [T.SeV2ClarificationMessageToBuyer]: { icon: FaComment, colorScheme: "violet" },
  [T.SeV2ResponsibleBuyerChanged]: { icon: FaUserEdit, colorScheme: "blue" },
  [T.SeV2JuryConfirmationRequested]: { icon: FaUserEdit, colorScheme: "amber" },
  [T.SeV2DoffinPublished]: { icon: FaBullhorn, colorScheme: "green" },
  [T.SeV2DoffinPublicationFailed]: { icon: FaTimes, colorScheme: "red" },
  [T.OrganizationMerge]: { icon: FaClone, colorScheme: "violet" },
};

const FALLBACK: NotificationVisual = { icon: FaBell, colorScheme: "neutral" };
export const notificationVisual = (type: NotificationTypeDto): NotificationVisual => MAP[type] ?? FALLBACK;

// Chip background/foreground per color — semantic tokens for dark-mode awareness.
export const CHIP_BG: Record<NotificationChipColor, string> = {
  teal: "smChipTealBg", amber: "smChipAmberBg", blue: "smChipBlueBg", green: "smChipGreenBg", red: "smChipRedBg", violet: "smChipVioletBg", neutral: "smBackgroundTertiary",
};
export const CHIP_FG: Record<NotificationChipColor, string> = {
  teal: "smChipTealFg", amber: "smChipAmberFg", blue: "smChipBlueFg", green: "smChipGreenFg", red: "smChipRedFg", violet: "smChipVioletFg", neutral: "smMuted",
};
